import { type ReactNode, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

type ModalSize = "sm" | "md" | "lg" | "xl";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  size?: ModalSize;
  className?: string;
  bodyClassName?: string;
  hideCloseButton?: boolean;
}

const sizeMap: Record<ModalSize, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
};

const mergeClassNames = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(" ");

const Modal = ({
  open,
  onClose,
  title,
  description,
  size = "md",
  className,
  bodyClassName,
  hideCloseButton = false,
  children,
}: React.PropsWithChildren<ModalProps>) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const target = useMemo(() => {
    if (typeof document === "undefined") {
      return null;
    }
    let root = document.getElementById("studict-modal-root");
    if (!root) {
      root = document.createElement("div");
      root.setAttribute("id", "studict-modal-root");
      document.body.appendChild(root);
    }
    return root;
  }, []);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!shouldRender || typeof window === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [shouldRender, onClose]);

  if (!shouldRender || !target) {
    return null;
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-[1200] flex items-center justify-center px-4 py-8 transition-opacity duration-300 ${
        isAnimating ? "opacity-100" : "opacity-0"
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : "Dialog"}
    >
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={mergeClassNames(
          "relative w-full transform overflow-hidden rounded-2xl bg-white text-text-main shadow-2xl transition-all duration-300",
          isAnimating ? "opacity-100 scale-100" : "opacity-0 scale-95",
          sizeMap[size],
          className,
        )}
      >
        {!hideCloseButton && (
          <button
            type="button"
            onClick={onClose}
            className="absolute -right-10 bottom-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface-200 text-text-main transition hover:bg-surface-300"
            aria-label="Close dialog"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {(title ?? description) && (
          <div className="border-b border-surface-300 px-6 py-5">
            {title && <div className="text-lg font-semibold text-text-main">{title}</div>}
            {description && <p className="mt-1 text-sm text-text-muted">{description}</p>}
          </div>
        )}
        <div className={mergeClassNames("px-6 py-6", bodyClassName)}>{children}</div>
      </div>
    </div>,
    target,
  );
};

export default Modal;
